import { useEffect, useState } from 'react';
import { CTAButton } from './CTAButton';
import { BookOpenIcon, ClipboardListIcon, HeartIcon, SparklesIcon, WalletIcon, ZapIcon, MessageCircleIcon, AlertTriangleIcon, FrownIcon, PiggyBankIcon, TrendingDownIcon, CheckCircleIcon, DownloadIcon, GiftIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
// Testimonials data
const testimonials = [{
  id: 1,
  initials: 'MC',
  name: 'María C.',
  location: 'Ciudad de México, México',
  text: 'Por primera vez en mi vida llegué a fin de quincena con dinero. El método de los sobres me cambió todo. Ya no vivo con ansiedad cada vez que miro mi cuenta.',
  context: '2 quincenas aplicando el método · Oct 2024'
}, {
  id: 2,
  initials: 'AR',
  name: 'Andrea R.',
  location: 'Bogotá, Colombia',
  text: 'Entendí en qué se me iba el dinero y dejé de vivir en rojo. Ahora separo mi quincena en sobres y no me quedo sin nada a mitad de mes. ¡Es increíble!',
  context: 'Salió del rojo en 1 mes · Sep 2024'
}, {
  id: 3,
  initials: 'LP',
  name: 'Lucía P.',
  location: 'Lima, Perú',
  text: 'Dejé de sentir ansiedad cada vez que miraba la app del banco. El sobre de emergencias me salvó cuando tuve un imprevisto. Ya no vivo al día.',
  context: '3 meses usando el método · Ago 2024'
}, {
  id: 4,
  initials: 'CV',
  name: 'Carolina V.',
  location: 'Santiago, Chile',
  text: 'Ahora puedo darme gustitos sin culpa porque tengo un sobre para eso. Antes me sentía mal cada vez que me compraba algo. ¡Esto cambió mi vida!',
  context: 'Primer ahorro en 6 semanas · Oct 2024'
}];
// Bonuses data
const bonuses = [{
  id: 0,
  title: 'BONO #1 — Guía de 30 Fugas Invisibles',
  description: 'Si tu quincena "se evapora", esto es el mapa que te muestra exactamente dónde se está yendo.',
  bullets: ['Detecta 30 fugas reales (las que "no se sienten" pero te dejan en cero)', 'Prioriza las 5 que más drenan y córtalas sin privarte de vivir', 'Mini plan 7 días + checklist para recuperar control rápido']
}, {
  id: 1,
  title: 'BONO #2 — Sobre Extra: "Por Si Las Dudas"',
  description: 'El sobre que te salva cuando la vida te pega y te iba a tocar endeudarte.',
  bullets: ['Reglas claras: qué SÍ cuenta como emergencia y qué NO', 'Define un monto meta simple (sin números raros)', 'Micro-aportes para llenarlo sin sentir castigo']
}, {
  id: 2,
  title: 'BONO #3 — Kit "Vida Variable" (límites suaves)',
  description: 'Lo variable deja de comerse tu quincena: aquí le pones paredes sin volverte robot.',
  bullets: ['Topes humanos por categoría (antojos, transporte, domicilios, tiendita)', '"Sí permitido" para gastar sin culpa (sin sabotaje)', 'Plan B cuando se te desordena la semana (sin empezar de cero)']
}, {
  id: 3,
  title: 'BONO #4 — Guía de Emergencias Pequeñas',
  description: 'Imprevisto pequeño ≠ quincena destruida. Esto te da un protocolo para no caer en pánico.',
  bullets: ['Tope por evento para que no se te vaya todo de golpe', 'Reposición en mini para recuperar el fondo rápido', 'Regla anti-ansiedad: cuándo usarlo y cuándo aguantar']
}, {
  id: 4,
  title: 'BONO #5 — Plan Deuda: Mínimo + Ataque',
  description: 'Deja de pagar "para sobrevivir" y empieza a ver avance real cada quincena.',
  bullets: ['Pagas mínimos sin ahogarte + atacas UNA deuda con intención', 'Estrategia simple para cerrar deudas más rápido (sin Excel)', 'Plantilla lista para ejecutar desde tu próxima quincena']
}];
export function SalesPage() {
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const handleAddToCart = () => {
    // Open checkout page with checkoutMode=10
    window.open('https://pay.hotmart.com/C103397678W?checkoutMode=10', '_blank');
  };
  // Testimonials carousel state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  // Bonus accordion state
  const [activeBonus, setActiveBonus] = useState(0);
  const toggleBonus = (index: number) => {
    setActiveBonus(activeBonus === index ? -1 : index);
  };
  // Autoplay effect
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % testimonials.length);
      }, 5000); // 5 seconds
      return () => clearInterval(interval);
    }
  }, [isPaused]);
  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % testimonials.length);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };
  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      nextSlide();
    }
    if (touchStart - touchEnd < -75) {
      prevSlide();
    }
  };
  // Load Wistia scripts
  useEffect(() => {
    // Load Wistia player script
    const playerScript = document.createElement('script');
    playerScript.src = 'https://fast.wistia.com/player.js';
    playerScript.async = true;
    document.head.appendChild(playerScript);
    // Load Wistia embed script
    const embedScript = document.createElement('script');
    embedScript.src = 'https://fast.wistia.com/embed/k1wfq873n7.js';
    embedScript.async = true;
    embedScript.type = 'module';
    document.head.appendChild(embedScript);
    return () => {
      // Cleanup scripts on unmount
      if (document.head.contains(playerScript)) {
        document.head.removeChild(playerScript);
      }
      if (document.head.contains(embedScript)) {
        document.head.removeChild(embedScript);
      }
    };
  }, []);
  return <div className="flex flex-col items-center w-full min-h-screen bg-cream-50 text-gray-800 font-sans" data-id="element-11">
      {/* Top Header Banner */}
      <div className="w-full bg-gradient-to-r from-green-600 to-green-700 py-4 px-4 text-center shadow-sm" data-id="element-12">
        <p className="text-white font-bold text-lg md:text-xl lg:text-2xl" data-id="element-13">
          Pagas 1 y recibes 6 ebooks 🎁 • Acceso inmediato ⚡
        </p>
      </div>

      {/* Hero Section */}
      <section className="w-full max-w-4xl mx-auto px-4 py-8 md:py-10" data-id="element-14">
        {/* Wistia Video */}
        <div className="mb-8 max-w-3xl mx-auto" data-id="element-15">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-center mb-6 leading-tight" data-id="element-16">
            De quincena en <span className="text-red-600" data-id="element-17">rojo</span> a quincena
            en{' '}
            <span style={{
            color: '#166534'
          }} data-id="element-18">
              verde
            </span>
          </h2>
          <style dangerouslySetInnerHTML={{
          __html: `
            wistia-player[media-id='k1wfq873n7']:not(:defined) { 
              background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/k1wfq873n7/swatch'); 
              display: block; 
              filter: blur(5px); 
              padding-top:56.25%; 
            }
          `
        }} data-id="element-19" />
          <wistia-player media-id="k1wfq873n7" aspect="1.7777777777777777" data-id="element-20"></wistia-player>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-21">
          <div className="flex-1" data-id="element-22">
            <h1 className="text-xl md:text-2xl font-bold text-green-800 mb-4 text-center" data-id="element-23">
              ¿Por qué sigues llegando sin dinero… aunque trabajas duro cada
              quincena?
            </h1>
            <p className="text-lg mb-6" data-id="element-24">
              Si estás leyendo esto, es porque estás cansada.
            </p>
            <p className="text-lg mb-6" data-id="element-25">
              Cansada de esforzarte tanto… para volver a vivir la misma historia
              cada quincena:
            </p>
            <p className="text-lg font-semibold mb-6 text-red-700" data-id="element-26">
              sin dinero
              <br data-id="element-27" />
              sin claridad
              <br data-id="element-28" />
              sin entender en qué se fue
            </p>
            <p className="text-lg mb-6" data-id="element-29">
              y repitiéndote: "ahora sí me voy a organizar"
            </p>
            <p className="text-lg mb-6" data-id="element-30">
              Pero llega otra quincena… y nada cambia.
            </p>
            <p className="text-lg mb-6" data-id="element-31">Y vuelve esa pregunta dolorosa:</p>
            <p className="text-lg font-semibold mb-6 italic" data-id="element-32">
              "¿Qué estoy haciendo mal?"
            </p>
          </div>
          <div className="flex-1 flex justify-center" data-id="element-33">
            <div className="flex flex-col items-center" data-id="element-34">
              <img src="/451d57e3-e835-4d16-86d2-ed16e8fdb89d.png" alt="De Quincena en Rojo a Quincena en Verde" className="w-full max-w-xs shadow-lg transform hover:rotate-2 transition-transform duration-300" data-id="element-35" />
              <button onClick={scrollToPricing} className="mt-6 bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-8 rounded-lg text-lg shadow-lg transform hover:scale-105 transition-all duration-300" data-id="element-36">
                Descubre Cómo
              </button>
            </div>
          </div>
        </div>
        <div className="mt-8 md:mt-10 max-w-2xl mx-auto" data-id="element-37">
          <h2 className="text-2xl md:text-3xl font-bold text-green-800 mb-6" data-id="element-38">
            La verdad es dura, pero también liberadora:
          </h2>
          <p className="text-xl font-bold mb-6" data-id="element-39">
            NO eres tú. NO es tu salario. NO es tu disciplina. Es que te falta
            un sistema.
          </p>
          <p className="text-lg mb-8" data-id="element-40">
            Un sistema simple. Visual. Hecho para ti. Un sistema que por fin te
            ayude a controlar tu dinero… y no que tu dinero te controle a ti.
          </p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="w-full bg-red-50 py-8" data-id="element-41">
        <div className="max-w-5xl mx-auto px-4" data-id="element-42">
          <h2 className="text-2xl md:text-3xl font-bold text-red-800 mb-4 text-center" data-id="element-43">
            La verdad incómoda que nadie te dice
          </h2>
          <p className="text-lg mb-3 text-center text-gray-700" data-id="element-44">
            No es que gastes de más. No es que seas mala administrando. No es
            que seas desordenada.
          </p>
          <p className="text-lg mb-3 text-center text-gray-700" data-id="element-45">
            Lo que pasa es que estás sobreviviendo sin un mapa.
          </p>
          <p className="text-lg mb-8 text-center text-gray-700 font-medium" data-id="element-46">
            Estás cruzando tu vida financiera con los ojos vendados.
          </p>
          <p className="text-xl font-semibold mb-6 text-center text-red-800" data-id="element-47">
            Y eso te deja atrapada en:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6" data-id="element-48">
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-49">
              <div className="flex flex-col items-center text-center" data-id="element-50">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-51">
                  <ZapIcon className="w-8 h-8 text-red-600" data-id="element-52" />
                </div>
                <h3 className="text-lg font-bold text-red-800 mb-2" data-id="element-53">
                  Estrés constante
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-54">
                  Viviendo con ansiedad financiera cada día
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-55">
              <div className="flex flex-col items-center text-center" data-id="element-56">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-57">
                  <MessageCircleIcon className="w-8 h-8 text-red-600" data-id="element-58" />
                </div>
                <h3 className="text-lg font-bold text-red-800 mb-2" data-id="element-59">
                  Discusiones por dinero
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-60">
                  Conflictos constantes que dañan tus relaciones
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-61">
              <div className="flex flex-col items-center text-center" data-id="element-62">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-63">
                  <AlertTriangleIcon className="w-8 h-8 text-red-600" data-id="element-64" />
                </div>
                <h3 className="text-lg font-bold text-red-800 mb-2" data-id="element-65">
                  Miedo a emergencias
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-66">
                  Sin colchón financiero para imprevistos
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-67">
              <div className="flex flex-col items-center text-center" data-id="element-68">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-69">
                  <FrownIcon className="w-8 h-8 text-red-600" data-id="element-70" />
                </div>
                <h3 className="text-lg font-bold text-red-800 mb-2" data-id="element-71">
                  Sentimiento de fracaso
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-72">
                  Sintiéndote incapaz de controlar tu dinero
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-73">
              <div className="flex flex-col items-center text-center" data-id="element-74">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-75">
                  <PiggyBankIcon className="w-8 h-8 text-red-600" data-id="element-76" />
                </div>
                <h3 className="text-lg font-bold text-red-800 mb-2" data-id="element-77">
                  Cero ahorro
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-78">
                  Sin poder guardar ni un peso para el futuro
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-79">
              <div className="flex flex-col items-center text-center" data-id="element-80">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-81">
                  <TrendingDownIcon className="w-8 h-8 text-red-600" data-id="element-82" />
                </div>
                <h3 className="text-lg font-bold text-red-800 mb-2" data-id="element-83">
                  "Nunca avanzo"
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-84">
                  Esa dolorosa sensación de estar estancada
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-8 shadow-md text-center max-w-2xl mx-auto" data-id="element-85">
            <p className="text-xl font-semibold text-gray-800" data-id="element-86">
              Eso pesa. Eso duele. Pero también se puede cambiar.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="w-full bg-green-50 py-8" data-id="element-87">
        <div className="max-w-5xl mx-auto px-4" data-id="element-88">
          <h2 className="text-2xl md:text-3xl font-bold text-green-800 mb-4 text-center" data-id="element-89">
            Imagina esto por un momento…
          </h2>
          <p className="text-lg mb-12 text-center text-gray-600 max-w-3xl mx-auto" data-id="element-90">
            El día que te pagan, haces algo distinto. En vez de gastar "como va
            saliendo", organizas tu dinero con un sistema visual que transforma
            tu vida financiera.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-91">
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow" data-id="element-92">
              <div className="flex items-start gap-4" data-id="element-93">
                <div className="flex-shrink-0" data-id="element-94">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-95">
                    <WalletIcon className="w-6 h-6 text-green-600" data-id="element-96" />
                  </div>
                </div>
                <div data-id="element-97">
                  <h3 className="text-xl font-bold text-green-800 mb-2" data-id="element-98">
                    Separa tu dinero en sobres
                  </h3>
                  <p className="text-gray-600" data-id="element-99">
                    Renta y servicios, mercado, transporte, tiendita,
                    emergencias y ahorro. Cada peso tiene un nombre y un lugar
                    específico.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow" data-id="element-100">
              <div className="flex items-start gap-4" data-id="element-101">
                <div className="flex-shrink-0" data-id="element-102">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-103">
                    <ClipboardListIcon className="w-6 h-6 text-green-600" data-id="element-104" />
                  </div>
                </div>
                <div data-id="element-105">
                  <h3 className="text-xl font-bold text-green-800 mb-2" data-id="element-106">
                    Control total de tus gastos
                  </h3>
                  <p className="text-gray-600" data-id="element-107">
                    Sistema visual que te permite ver exactamente cuánto tienes
                    para cada categoría. Sin apps complicadas, sin estrés.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow" data-id="element-108">
              <div className="flex items-start gap-4" data-id="element-109">
                <div className="flex-shrink-0" data-id="element-110">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-111">
                    <HeartIcon className="w-6 h-6 text-green-600" data-id="element-112" />
                  </div>
                </div>
                <div data-id="element-113">
                  <h3 className="text-xl font-bold text-green-800 mb-2" data-id="element-114">
                    Gustitos sin culpa
                  </h3>
                  <p className="text-gray-600" data-id="element-115">
                    Tu sobre favorito: dinero destinado para darte gustos sin
                    sentirte mal. Porque mereces disfrutar tu dinero también.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow" data-id="element-116">
              <div className="flex items-start gap-4" data-id="element-117">
                <div className="flex-shrink-0" data-id="element-118">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-119">
                    <SparklesIcon className="w-6 h-6 text-green-600" data-id="element-120" />
                  </div>
                </div>
                <div data-id="element-121">
                  <h3 className="text-xl font-bold text-green-800 mb-2" data-id="element-122">
                    Llega con dinero sobrando
                  </h3>
                  <p className="text-gray-600" data-id="element-123">
                    Por primera vez en tu vida, llegas a la siguiente quincena
                    con dinero disponible. Ese día recuperas tu paz financiera.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-8 text-white text-center" data-id="element-124">
            <p className="text-xl md:text-2xl font-bold mb-3" data-id="element-125">
              Cada peso tiene un nombre. Cada peso tiene un lugar. Cada peso
              tiene una misión.
            </p>
            <p className="text-lg opacity-90" data-id="element-126">
              Ese día entiendes que nunca ganaste poco… solo te faltaba un
              método.
            </p>
          </div>
        </div>
      </section>

      {/* Method Section */}
      <section className="w-full py-8" data-id="element-127">
        <div className="max-w-4xl mx-auto px-4" data-id="element-128">
          <h2 className="text-2xl md:text-3xl font-bold text-amber-700 mb-6 text-center" data-id="element-129">
            El método del sobre que cambia vidas
          </h2>
          <p className="text-lg mb-6" data-id="element-130">
            El "cash stuffing" se volvió tendencia en Estados Unidos… pero así
            como está, no funciona aquí.
          </p>
          <p className="text-lg mb-6" data-id="element-131">¿Por qué?</p>
          <p className="text-lg mb-6" data-id="element-132">Porque la vida aquí es distinta.</p>
          <p className="text-lg mb-8" data-id="element-133">
            Aquí existe la tiendita. Aquí se paga en efectivo. Aquí los
            antojitos importan. Aquí el transporte cambia cada semana. Aquí las
            pequeñas fugas arruinan meses completos.
          </p>
          <p className="text-lg mb-6" data-id="element-134">Por eso nació este sistema:</p>
          <div className="bg-green-100 border-l-4 border-green-600 p-6 rounded-r-lg mb-8" data-id="element-135">
            <p className="text-xl font-bold text-green-800 mb-2" data-id="element-136">
              El Método del Sobre
            </p>
            <p className="text-lg text-green-700" data-id="element-137">
              (adaptado totalmente a tu vida real)
            </p>
            <p className="mt-4" data-id="element-138">
              Es visual. Es práctico. Es simple. Y funciona.
            </p>
          </div>
          <div className="bg-gradient-to-r from-green-700 to-green-600 text-white p-6 rounded-lg mb-6" data-id="element-139">
            <h3 className="text-xl font-bold mb-4" data-id="element-140">
              De Quincena en Rojo a Quincena en Verde
            </h3>
            <p className="mb-4" data-id="element-141">
              La guía práctica que te ayuda a dejar de vivir "sin dinero" y
              empezar a ver verde en tu billetera.
            </p>
          </div>
          <div className="flex justify-center" data-id="element-142">
            <button onClick={scrollToPricing} className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-8 rounded-lg text-lg shadow-lg transform hover:scale-105 transition-all duration-300" data-id="element-143">
              Descubre como
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="w-full bg-amber-50 py-8" data-id="element-144">
        <div className="max-w-5xl mx-auto px-4" data-id="element-145">
          <h2 className="text-2xl md:text-3xl font-bold text-amber-800 mb-8 text-center" data-id="element-146">
            ¿Qué vas a aprender dentro?
          </h2>
          {/* Main Benefits Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-147">
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-148">
              <div className="flex gap-4" data-id="element-149">
                <div className="flex-shrink-0" data-id="element-150">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-151">
                    <BookOpenIcon className="w-6 h-6 text-green-700" data-id="element-152" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-153">
                  <div className="flex items-start gap-2 mb-2" data-id="element-154">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-155" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-156">
                      Explicaciones simples
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-157">
                    Sin tecnicismos financieros, todo explicado de forma clara y
                    práctica
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-158">
              <div className="flex gap-4" data-id="element-159">
                <div className="flex-shrink-0" data-id="element-160">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-161">
                    <WalletIcon className="w-6 h-6 text-green-700" data-id="element-162" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-163">
                  <div className="flex items-start gap-2 mb-2" data-id="element-164">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-165" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-166">
                      Método del Sobre adaptado
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-167">
                    Sistema adaptado a tu realidad: tiendita, transporte y
                    gastos reales
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-168">
              <div className="flex gap-4" data-id="element-169">
                <div className="flex-shrink-0" data-id="element-170">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-171">
                    <ClipboardListIcon className="w-6 h-6 text-green-700" data-id="element-172" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-173">
                  <div className="flex items-start gap-2 mb-2" data-id="element-174">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-175" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-176">
                      Ejercicios prácticos paso a paso
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-177">
                    Actividades guiadas para implementar el método desde el día
                    1
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-178">
              <div className="flex gap-4" data-id="element-179">
                <div className="flex-shrink-0" data-id="element-180">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-181">
                    <DownloadIcon className="w-6 h-6 text-green-700" data-id="element-182" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-183">
                  <div className="flex items-start gap-2 mb-2" data-id="element-184">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-185" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-186">
                      Plantillas listas para imprimir
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-187">
                    Sobres, trackers y herramientas visuales que puedes usar de
                    inmediato
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-188">
              <div className="flex gap-4" data-id="element-189">
                <div className="flex-shrink-0" data-id="element-190">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-191">
                    <PiggyBankIcon className="w-6 h-6 text-green-700" data-id="element-192" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-193">
                  <div className="flex items-start gap-2 mb-2" data-id="element-194">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-195" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-196">
                      Reto de ahorro quincenal
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-197">
                    Desafío progresivo para construir tu colchón financiero sin
                    sufrimiento
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-198">
              <div className="flex gap-4" data-id="element-199">
                <div className="flex-shrink-0" data-id="element-200">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-201">
                    <ZapIcon className="w-6 h-6 text-green-700" data-id="element-202" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-203">
                  <div className="flex items-start gap-2 mb-2" data-id="element-204">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-205" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-206">
                      Elimina fugas invisibles
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-207">
                    Identifica y detén los gastos hormiga que te roban la
                    quincena
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-208">
              <div className="flex gap-4" data-id="element-209">
                <div className="flex-shrink-0" data-id="element-210">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-211">
                    <HeartIcon className="w-6 h-6 text-green-700" data-id="element-212" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-213">
                  <div className="flex items-start gap-2 mb-2" data-id="element-214">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-215" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-216">
                      Gustitos sin culpa
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-217">
                    Aprende a darte gustos planificados sin descontrolar tu
                    presupuesto
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-218">
              <div className="flex gap-4" data-id="element-219">
                <div className="flex-shrink-0" data-id="element-220">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-221">
                    <AlertTriangleIcon className="w-6 h-6 text-green-700" data-id="element-222" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-223">
                  <div className="flex items-start gap-2 mb-2" data-id="element-224">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-225" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-226">
                      Crea tu fondo de emergencia
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm" data-id="element-227">
                    Construye tu colchón financiero para imprevistos sin estrés
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Simple Checkmarks */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-228">
            <div className="space-y-3" data-id="element-229">
              <div className="flex items-center gap-3" data-id="element-230">
                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-231" />
                <span className="text-gray-700" data-id="element-232">
                  Formato digital - descarga inmediata
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-233">
                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-234" />
                <span className="text-gray-700" data-id="element-235">
                  Actualizado con las últimas estrategias financieras
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-236">
                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-237" />
                <span className="text-gray-700" data-id="element-238">
                  Acceso de por vida y actualizaciones gratuitas
                </span>
              </div>
            </div>
          </div>
          <h3 className="text-xl font-bold text-green-800 mb-4 text-center" data-id="element-239">
            Por qué funciona tan rápido
          </h3>
          <p className="text-lg mb-6" data-id="element-240">
            Porque no te pide hacer cosas imposibles.
          </p>
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6" data-id="element-241">
            <div className="space-y-2 text-gray-700" data-id="element-242">
              <p data-id="element-243">❌ No te pide dejar antojitos.</p>
              <p data-id="element-244">❌ No te pide hacer dietas financieras.</p>
              <p data-id="element-245">❌ No te pide aprender contabilidad.</p>
              <p data-id="element-246">❌ No te pide revisar cada peso de memoria.</p>
            </div>
          </div>
          <p className="text-lg mb-4 font-medium text-green-800" data-id="element-247">
            Lo que te pide es algo muchísimo más simple:
          </p>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-600 p-6 rounded-r-lg shadow-sm" data-id="element-248">
            <div className="space-y-2" data-id="element-249">
              <p className="flex items-start gap-2" data-id="element-250">
                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-251" />
                <span data-id="element-252">Separar tu dinero antes de gastarlo.</span>
              </p>
              <p className="flex items-start gap-2" data-id="element-253">
                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-254" />
                <span data-id="element-255">Darle un propósito a cada peso.</span>
              </p>
              <p className="flex items-start gap-2" data-id="element-256">
                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-257" />
                <span data-id="element-258">
                  Seguir un sistema visual que no requiere esfuerzo mental.
                </span>
              </p>
            </div>
          </div>
          <p className="text-lg font-bold text-green-800 mt-6 text-center" data-id="element-259">
            Eso es todo. Y funciona.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full py-6 bg-gray-50" data-id="element-260">
        <div className="max-w-4xl mx-auto px-4" data-id="element-261">
          <h2 className="text-2xl md:text-3xl font-bold text-green-800 mb-8 text-center" data-id="element-262">
            Preguntas frecuentes
          </h2>
          <div className="space-y-6" data-id="element-263">
            <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-264">
              <h3 className="text-xl font-bold mb-3 text-amber-700" data-id="element-265">
                ¿Es posible ahorrar desde la próxima quincena?
              </h3>
              <p data-id="element-266">Sí. Miles de mujeres ya lo están logrando con este método.</p>
              <p className="mt-2" data-id="element-267">Y tú puedes ser la siguiente.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-268">
              <h3 className="text-xl font-bold mb-3 text-amber-700" data-id="element-269">
                "Pero, ¿y si gano poco?"
              </h3>
              <p data-id="element-270">Ese es uno de los grandes mitos del dinero.</p>
              <p className="mt-3" data-id="element-271">La verdad es esta:</p>
              <p className="mt-3 font-medium" data-id="element-272">
                No importa cuánto ganes. Importa cómo organizas lo que recibes.
              </p>
              <p className="mt-3" data-id="element-273">Ganar más sin método = gastar más.</p>
              <p data-id="element-274">Ganar lo mismo con método = control total.</p>
            </div>
          </div>
          <div className="mt-8 bg-green-100 p-6 rounded-lg border-l-4 border-green-600" data-id="element-275">
            <p className="text-lg mb-4" data-id="element-276">
              Este ebook no es un gasto. Es una decisión que cambia tu vida
              financiera.
            </p>
            <p className="text-lg" data-id="element-277">
              Te acompaña cada quincena. Te da estructura. Te da claridad. Y,
              sobre todo… te devuelve la paz.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonial/Story Section */}
      <section className="w-full py-8" data-id="element-278">
        <div className="max-w-4xl mx-auto px-4" data-id="element-279">
          <h2 className="text-2xl md:text-3xl font-bold text-green-800 mb-6" data-id="element-280">
            Mi historia (y por qué sé exactamente cómo te sientes)
          </h2>
          <div className="bg-white p-6 rounded-lg shadow-md mb-8" data-id="element-281">
            <p className="mb-4" data-id="element-282">
              Hace unos años, cuando empecé a cobrar por quincena, vivía igual
              que tú: estresada, agobiada y con miedo de que no me alcanzara.
            </p>
            <p className="mb-4" data-id="element-283">
              Pero un día, después de una quincena especialmente dura, revisé
              mis cuentas y algo me rompió por dentro:
            </p>
            <div className="pl-4 border-l-4 border-red-500 my-6" data-id="element-284">
              <p className="mb-2 text-red-700" data-id="element-285">
                Gané bien… pero terminé con cero.
              </p>
              <p className="mb-2 text-red-700" data-id="element-286">No sabía en qué se me fue.</p>
              <p className="mb-2 text-red-700" data-id="element-287">Me sentía fracasada.</p>
              <p className="text-red-700" data-id="element-288">
                Sentí que había perdido el control de mi propia vida.
              </p>
            </div>
            <p className="mb-4" data-id="element-289">
              Esa noche lloré en silencio. No por el dinero. Sino por sentirme
              fuera de control.
            </p>
            <p className="mb-4" data-id="element-290">
              Busqué soluciones. Probé apps. Probé cursos. Probé métodos
              complicados. Nada funcionaba.
            </p>
            <p className="mb-4" data-id="element-291">
              Hasta que descubrí el "cash stuffing". Me encantó. Pero era
              demasiado gringo. No hablaba de tiendita, antojitos, transporte ni
              efectivo. No era mi realidad.
            </p>
            <p className="mb-4" data-id="element-292">
              Así que lo adapté. Lo hice simple. Lo hice visual. Lo hice latino.
            </p>
            <p className="mb-4" data-id="element-293">
              Y en la primera quincena… ahorré por primera vez en mi vida.
            </p>
            <p className="mb-4" data-id="element-294">
              Mis amigas lo pidieron. Mis primas también. Se volvió un método.
            </p>
            <p className="text-green-700 font-medium" data-id="element-295">
              Y ahora está aquí… convertido en un ebook que ya está ayudando a
              miles de mujeres a transformar su vida financiera.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section - CAROUSEL */}
      <section className="w-full py-12 bg-gradient-to-b from-green-800 to-green-900" data-id="element-296">
        <div className="max-w-6xl mx-auto px-4" data-id="element-297">
          {/* Header */}
          <div className="text-center mb-12" data-id="element-298">
            <h2 className="text-3xl md:text-4xl font-bold text-amber-400 mb-4" data-id="element-299">
              Lo que dicen quienes ya usan la guía
            </h2>
            <p className="text-lg text-green-100" data-id="element-300">
              Mujeres reales que transformaron su relación con el dinero
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative max-w-5xl mx-auto" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-301">
            {/* Carousel Track */}
            <div className="overflow-hidden" data-id="element-302">
              <div className="flex transition-transform duration-500 ease-in-out" style={{
              transform: `translateX(-${currentIndex * 100}%)`
            }} data-id="element-303">
                {testimonials.map(testimonial => <div key={testimonial.id} className="w-full md:w-1/2 flex-shrink-0 px-3" data-id="element-304">
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 shadow-lg h-full" data-id="element-305">
                      {/* Stars */}
                      <div className="flex gap-1 mb-3" data-id="element-306">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-amber-400 text-amber-400" data-id="element-307" />)}
                      </div>

                      {/* Verified Badge */}
                      <span className="inline-block mb-4 px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full" data-id="element-308">
                        ✓ Compra verificada
                      </span>

                      {/* Testimonial Text */}
                      <p className="text-gray-800 mb-4 leading-relaxed text-base" data-id="element-309">
                        "{testimonial.text}"
                      </p>

                      {/* Author Info */}
                      <div className="flex items-center gap-3 mb-3" data-id="element-310">
                        <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-311">
                          <span className="text-white font-bold text-sm" data-id="element-312">
                            {testimonial.initials}
                          </span>
                        </div>
                        <div data-id="element-313">
                          <h3 className="font-bold text-gray-900" data-id="element-314">
                            {testimonial.name}
                          </h3>
                          <p className="text-xs text-gray-600" data-id="element-315">
                            {testimonial.location}
                          </p>
                        </div>
                      </div>

                      {/* Context */}
                      <p className="text-xs text-gray-500 font-medium" data-id="element-316">
                        {testimonial.context}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Navigation Arrows - Desktop */}
            <button onClick={prevSlide} className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-amber-50 transition-colors z-10" aria-label="Anterior" data-id="element-317">
              <ChevronLeftIcon className="w-6 h-6 text-green-800" data-id="element-318" />
            </button>
            <button onClick={nextSlide} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-amber-50 transition-colors z-10" aria-label="Siguiente" data-id="element-319">
              <ChevronRightIcon className="w-6 h-6 text-green-800" data-id="element-320" />
            </button>
          </div>

          {/* Dots Indicators */}
          <div className="flex justify-center gap-2 mt-8" data-id="element-321">
            {testimonials.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`w-2.5 h-2.5 rounded-full transition-all ${index === currentIndex ? 'bg-amber-400 w-8' : 'bg-green-300 hover:bg-green-200'}`} aria-label={`Ir al testimonio ${index + 1}`} data-id="element-322" />)}
          </div>
        </div>
      </section>

      {/* Pricing/Purchase Section - REORGANIZED */}
      <section id="pricing-section" className="w-full py-10 bg-gradient-to-b from-white to-green-50" data-id="element-323">
        <div className="max-w-5xl mx-auto px-4" data-id="element-324">
          <div className="text-center mb-8" data-id="element-325">
            <h2 className="text-3xl md:text-4xl font-bold text-green-800 mb-4" data-id="element-326">
              Obtén tu guía completa hoy
            </h2>
            <p className="text-xl text-gray-700" data-id="element-327">
              Todo lo que necesitas para transformar tu vida financiera
            </p>
          </div>

          {/* SINGLE VERTICAL CONTAINER */}
          <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl shadow-2xl overflow-hidden max-w-4xl mx-auto p-8 md:p-10 text-white" data-id="element-328">
            {/* 1. Product Image */}
            <div className="flex justify-center mb-8" data-id="element-329">
              <img src="/Mockup_Oferta.png" alt="De Quincena en Rojo a Quincena en Verde - Ebook" className="w-full max-w-sm rounded-lg shadow-xl transform hover:scale-105 transition-transform duration-300" data-id="element-330" />
            </div>

            {/* 2. Pricing & Title */}
            <div className="text-center mb-8" data-id="element-331">
              <h3 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-332">
                De Quincena en Rojo a Quincena en Verde
              </h3>
              <p className="text-green-100 mb-6 text-lg" data-id="element-333">
                Tu guía práctica para el control financiero
              </p>

              <div className="flex items-center justify-center gap-3 mb-2" data-id="element-334">
                <span className="text-2xl font-medium text-green-200 line-through opacity-75" data-id="element-335">
                  $15.94
                </span>
                <span className="text-5xl font-bold text-white" data-id="element-336">$7.97</span>
                <span className="text-green-200 text-xl" data-id="element-337">USD</span>
              </div>
              <p className="text-green-200 font-medium bg-green-800/30 inline-block px-4 py-1 rounded-full" data-id="element-338">
                ✨ Precio especial de lanzamiento
              </p>
            </div>

            {/* 3. Benefits */}
            <div className="space-y-3 mb-10 max-w-lg mx-auto" data-id="element-339">
              <div className="flex items-start gap-3" data-id="element-340">
                <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-0.5" data-id="element-341" />
                <span className="text-lg" data-id="element-342">Descarga inmediata en PDF</span>
              </div>
              <div className="flex items-start gap-3" data-id="element-343">
                <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-0.5" data-id="element-344" />
                <span className="text-lg" data-id="element-345">Plantillas listas para imprimir</span>
              </div>
              <div className="flex items-start gap-3" data-id="element-346">
                <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-0.5" data-id="element-347" />
                <span className="text-lg" data-id="element-348">
                  Método paso a paso adaptado a tu realidad
                </span>
              </div>
              <div className="flex items-start gap-3" data-id="element-349">
                <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-0.5" data-id="element-350" />
                <span className="text-lg" data-id="element-351">Acceso de por vida</span>
              </div>
            </div>

            {/* 4. Bonuses Header */}
            <div className="flex items-center justify-center gap-2 mb-6" data-id="element-352">
              <GiftIcon className="w-6 h-6 text-yellow-300" data-id="element-353" />
              <h3 className="text-xl md:text-2xl font-bold text-center text-yellow-300" data-id="element-354">
                +5 Bonos de regalo (incluidos hoy)
              </h3>
              <GiftIcon className="w-6 h-6 text-yellow-300" data-id="element-355" />
            </div>

            {/* Bonuses Image */}
            <div className="flex justify-center mb-8" data-id="element-356">
              <img src="/Diseno_sin_titulo_(1).png" alt="Bonos incluidos" className="w-full max-w-2xl rounded-xl shadow-lg" data-id="element-357" />
            </div>

            {/* Call to action text with animation */}
            <div className="text-center mb-6" data-id="element-358">
              <p className="text-lg md:text-xl font-semibold text-yellow-300 animate-bounce" data-id="element-359">
                Toca para ver lo que incluye 👇
              </p>
            </div>

            {/* 5. BONUS Accordion Section */}
            <div className="w-full max-w-3xl mx-auto mb-8 space-y-3" data-id="element-360">
              {bonuses.map(bonus => <div key={bonus.id} className={`overflow-hidden rounded-xl transition-all duration-300 border border-green-500/30 ${activeBonus === bonus.id ? 'bg-white/10 shadow-lg' : 'bg-green-800/40 hover:bg-green-800/60'}`} data-id="element-361">
                  <button onClick={() => toggleBonus(bonus.id)} className="w-full flex items-center justify-between p-4 text-left focus:outline-none" aria-expanded={activeBonus === bonus.id} data-id="element-362">
                    <div className="flex items-center gap-3 pr-4" data-id="element-363">
                      <GiftIcon className={`w-5 h-5 flex-shrink-0 ${activeBonus === bonus.id ? 'text-yellow-300' : 'text-green-200'}`} data-id="element-364" />
                      <span className={`font-bold text-base md:text-lg ${activeBonus === bonus.id ? 'text-white' : 'text-white'}`} data-id="element-365">
                        {bonus.title}
                      </span>
                    </div>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border transition-colors ${activeBonus === bonus.id ? 'bg-[#f59e0b] border-[#f59e0b] text-white' : 'border-green-300 text-green-300'}`} data-id="element-366">
                      {activeBonus === bonus.id ? <span className="text-xl font-bold leading-none mb-0.5" data-id="element-367">
                          ×
                        </span> : <span className="text-xl font-bold leading-none mb-0.5" data-id="element-368">
                          +
                        </span>}
                    </div>
                  </button>

                  <div className={`transition-all duration-300 ease-in-out ${activeBonus === bonus.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-369">
                    <div className="px-4 pb-5 pt-0 pl-12" data-id="element-370">
                      <p className="text-white mb-3 text-base md:text-lg leading-relaxed" data-id="element-371">
                        {bonus.description}
                      </p>
                      <ul className="space-y-2" data-id="element-372">
                        {bonus.bullets.map((bullet, idx) => <li key={idx} className="flex items-start gap-2 text-base text-white" data-id="element-373">
                            <span className="text-yellow-300 mt-1" data-id="element-374">•</span>
                            <span data-id="element-375">{bullet}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* 7. Reinforcement Text */}
            <div className="text-center mb-6" data-id="element-376">
              <p className="text-lg text-green-100" data-id="element-377">
                <strong className="text-white" data-id="element-378">Menos de $8 USD</strong> para
                dejar de vivir con ansiedad cada quincena.
              </p>
            </div>

            {/* 8. CTA Button */}
            <button onClick={handleAddToCart} className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-6 rounded-lg text-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 mb-4" data-id="element-379">
              <DownloadIcon className="w-6 h-6" data-id="element-380" />
              Comprar Ahora
            </button>

            {/* 9. Security Text */}
            <p className="text-center text-sm text-green-200" data-id="element-381">
              🔒 Pago seguro • Descarga instantánea
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full py-8 bg-green-700 text-white" data-id="element-382">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-383">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" data-id="element-384">
            El cambio comienza ahora
          </h2>
          <p className="text-lg mb-8" data-id="element-385">
            No importa cómo llegaste hoy. Lo que importa es cómo quieres llegar
            a la próxima quincena.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-4 mb-8 text-center" data-id="element-386">
            <div className="flex-1 bg-green-800 bg-opacity-50 p-4 rounded-lg" data-id="element-387">
              <p data-id="element-388">Con miedo…</p>
              <p className="text-green-300" data-id="element-389">o con control total.</p>
            </div>
            <div className="flex-1 bg-green-800 bg-opacity-50 p-4 rounded-lg" data-id="element-390">
              <p data-id="element-391">Sin dinero…</p>
              <p className="text-green-300" data-id="element-392">o con ahorro real.</p>
            </div>
            <div className="flex-1 bg-green-800 bg-opacity-50 p-4 rounded-lg" data-id="element-393">
              <p data-id="element-394">Con estrés…</p>
              <p className="text-green-300" data-id="element-395">o con tranquilidad.</p>
            </div>
          </div>
          <p className="text-lg mb-8" data-id="element-396">
            Este es tu momento. Tu punto de quiebre. Tu nueva historia
            financiera comienza aquí.
          </p>
          <CTAButton data-id="element-397" />
          <div className="mt-8 text-center" data-id="element-398">
            <p className="text-xl font-bold mb-2" data-id="element-399">
              De Quincena en Rojo a Quincena en Verde
            </p>
            <p className="text-lg" data-id="element-400">
              Tu método, tu sistema, tu nueva forma de vivir la quincena.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full py-6 bg-green-900 text-white text-center" data-id="element-401">
        <div className="max-w-4xl mx-auto px-4" data-id="element-402">
          <p className="text-sm text-green-200" data-id="element-403">
            © 2023 De Quincena en Rojo a Quincena en Verde. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>
    </div>;
}